/**
 * Audio Engine for headphones and hearing test
 * Manages Web Audio API context and audio generation
 * Updated to use standardized error handling
 */
import { dbToGain as mathDbToGain } from './math-utils';
import { Logger } from './logger';
import { AudioError, getErrorHandler } from './error-handling';

const logger = Logger.createComponentLogger('AudioEngine');

class AudioEngine {
    audioContext: AudioContext | null;
    noiseNode: AudioBufferSourceNode | null;
    sineNode: OscillatorNode | null;
    noiseGainNode: GainNode | null;
    sineGainNode: GainNode | null;
    isStarted: boolean;
    // Playback mode: 'continuous' keeps the sine gain steady.
    // 'intermittent' toggles the sine gain on/off (1s on, 1s off).
    playbackMode: 'continuous' | 'intermittent';
    // Intermittent durations (seconds) — change these to adjust on/off timing
    playDurationSeconds: number;
    gapDurationSeconds: number;
    // Timer id for intermittent toggling (returned by setTimeout)
    intermittentTimer: number | null;
    // Tracks whether the intermittent cycle is currently in the "on" phase
    intermittentIsOn: boolean;
    // Stores the last requested linear gain corresponding to the sine volume slider
    sineTargetGain: number;

    constructor() {
        this.audioContext = null;
        this.noiseNode = null;
        this.sineNode = null;
        this.noiseGainNode = null;
        this.sineGainNode = null;
        this.isStarted = false;
        this.playbackMode = 'continuous';
        // default intermittent durations: 1s on, 1s off (modify these values)
        this.playDurationSeconds = 1;
        this.gapDurationSeconds = 0.5;
        this.intermittentTimer = null;
        this.intermittentIsOn = true;
        this.sineTargetGain = 0;
    }

    /**
     * Initialize the Web Audio API context and audio nodes
     */
    initAudio() {
        // Create AudioContext
        if (!this.audioContext) {
            try {
                (window as any).AudioContext = (window as any).AudioContext || (window as any).webkitAudioContext;
                this.audioContext = new AudioContext();
            } catch (e) {
                const audioError = new AudioError('AudioEngine', 'initAudio', 'Failed to create AudioContext', e instanceof Error ? e : undefined);
                getErrorHandler().handleErrorWithNotification(audioError, 'AudioEngine', 'initAudio', 'Web Audio API is not supported in this browser. Please use a modern browser for audio functionality.');
                throw audioError;
            }
        }

        // --- Noise Generator ---
        const bufferSize = 2 * this.audioContext.sampleRate;
        const noiseBuffer = this.audioContext.createBuffer(1, bufferSize, this.audioContext.sampleRate);
        const output = noiseBuffer.getChannelData(0);
        for (let i = 0; i < bufferSize; i++) {
            output[i] = Math.random() * 2 - 1;
        }

        this.noiseNode = this.audioContext.createBufferSource();
        this.noiseNode.buffer = noiseBuffer;
        this.noiseNode.loop = true;

        // --- Sine Wave Generator ---
        this.sineNode = this.audioContext.createOscillator();
        this.sineNode.type = 'sine';
        this.sineNode.frequency.setValueAtTime(1000, this.audioContext.currentTime);

        // --- Gain Nodes ---
        this.noiseGainNode = this.audioContext.createGain();
        this.sineGainNode = this.audioContext.createGain();

        this.noiseGainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
        this.sineGainNode.gain.setValueAtTime(0, this.audioContext.currentTime);

        // Connect nodes
        this.noiseNode.connect(this.noiseGainNode).connect(this.audioContext.destination);
        this.sineNode.connect(this.sineGainNode).connect(this.audioContext.destination);

        // Start sources
        try {
            this.noiseNode.start(0);
            this.sineNode.start(0);
        } catch (e) {
            // Ignore if already started - this is expected behavior
            logger.debug('Audio nodes already started, continuing...');
        }
    }

    /**
     * Start audio playback
     */
    startAudio() {
        if (!this.audioContext) {
            this.initAudio();
        }
        // Browsers may suspend the audio context after a period of inactivity.
        if (this.audioContext && this.audioContext.state === 'suspended') {
            this.audioContext.resume();
        }
        this.isStarted = true;

        // Apply playback mode behavior
        if (this.playbackMode === 'intermittent') {
            this.startIntermittent();
        } else {
            // Ensure continuous gain is applied
            if (this.sineGainNode && this.audioContext) {
                this.sineGainNode.gain.setTargetAtTime(this.sineTargetGain, this.audioContext.currentTime, 0.015);
            }
        }
    }

    /**
     * Stop audio playback
     */
    stopAudio() {
        if (this.audioContext) {
            // Clear intermittent behavior if running
            this.stopIntermittent();

            // Set gains to 0 to effectively stop the sound.
            if (this.noiseGainNode) this.noiseGainNode.gain.setTargetAtTime(0, this.audioContext.currentTime, 0.015);
            if (this.sineGainNode) this.sineGainNode.gain.setTargetAtTime(0, this.audioContext.currentTime, 0.015);
            this.isStarted = false;
        }
    }

    /**
     * Set noise volume in dB
     * @param {number} dbValue - Volume in decibels
     */
    setNoiseVolume(dbValue: number): void {
        if (this.noiseGainNode) {
            // Using setTargetAtTime for smooth, click-free volume changes
            const gain = this.dbToGain(dbValue);
            this.noiseGainNode.gain.setTargetAtTime(gain, this.audioContext.currentTime, 0.015);
        }
    }


    /**
     * Set sine wave frequency
     * @param {number} frequency - Frequency in Hz
     */
    setSineFrequency(frequency: number): void {
        if (this.sineNode && this.audioContext) {
            this.sineNode.frequency.setTargetAtTime(frequency, this.audioContext.currentTime, 0.015);
        }
    }

    /**
     * Set playback mode for the sine: 'continuous' | 'intermittent'
     * If audio is running, this will start/stop the intermittent toggling as needed.
     */
    setPlaybackMode(mode: 'continuous' | 'intermittent') {
        this.playbackMode = mode;
        if (!this.audioContext) {
            // AudioContext not yet initialized; mode will be applied when startAudio runs.
            return;
        }

        if (this.playbackMode === 'intermittent') {
            // Start intermittent toggling if audio is started
            if (this.isStarted) this.startIntermittent();
        } else {
            // Switch to continuous: stop intermittent toggling and set gain to target
            this.stopIntermittent();
            if (this.sineGainNode) {
                this.sineGainNode.gain.setTargetAtTime(this.sineTargetGain, this.audioContext.currentTime, 0.015);
            }
        }
    }
    
    /**
     * Set intermittent durations (seconds) for on/off phases.
     * @param playSeconds number seconds the sine stays ON
     * @param gapSeconds number seconds the sine stays OFF
     */
    setIntermittentDurations(playSeconds: number, gapSeconds: number): void {
        this.playDurationSeconds = Math.max(0, Number(playSeconds) || 0);
        this.gapDurationSeconds = Math.max(0, Number(gapSeconds) || 0);
        // If timer is running, restart schedule so new durations take effect immediately.
        if (this.isStarted && this.playbackMode === 'intermittent') {
            this.stopIntermittent();
            this.startIntermittent();
        }
    }
    
    /**
     * Set play (on) duration in seconds.
     */
    setPlayDurationSeconds(seconds: number): void {
        this.setIntermittentDurations(seconds, this.gapDurationSeconds);
    }
    
    /**
     * Set gap (off) duration in seconds.
     */
    setGapDurationSeconds(seconds: number): void {
        this.setIntermittentDurations(this.playDurationSeconds, seconds);
    }
    
    /**
     * Start intermittent toggling: alternate sine gain between target and 0 using configurable durations.
     * Uses setTargetAtTime with a small timeConstant to reduce clicks.
     * The on/off durations are controlled by `playDurationSeconds` and `gapDurationSeconds`.
     */
    startIntermittent() {
        if (!this.audioContext || !this.sineGainNode) return;
        // Clear any existing timer
        this.stopIntermittent();

        // Ensure we start in the "on" phase
        this.intermittentIsOn = true;
        // Apply the target gain immediately (smoothly)
        this.sineGainNode.gain.setTargetAtTime(this.sineTargetGain, this.audioContext.currentTime, 0.015);

        // Recursive timeout-based scheduler so durations can be changed easily.
        const scheduleNext = () => {
            if (!this.audioContext || !this.sineGainNode) return;
            const durationSeconds = this.intermittentIsOn ? this.playDurationSeconds : this.gapDurationSeconds;
            this.intermittentTimer = window.setTimeout(() => {
                if (!this.audioContext || !this.sineGainNode) return;
                const now = this.audioContext.currentTime;
                if (this.intermittentIsOn) {
                    // Turn off
                    this.sineGainNode.gain.setTargetAtTime(0, now, 0.02);
                    this.intermittentIsOn = false;
                } else {
                    // Turn on
                    this.sineGainNode.gain.setTargetAtTime(this.sineTargetGain, now, 0.02);
                    this.intermittentIsOn = true;
                }
                // Schedule the next toggle based on the (possibly updated) durations
                scheduleNext();
            }, Math.max(0, durationSeconds) * 1000) as unknown as number;
        };

        // Kick off the first scheduled toggle after the initial "on" period
        scheduleNext();
    }

    /**
     * Stop intermittent toggling and clear timers.
     */
    stopIntermittent() {
        if (this.intermittentTimer !== null) {
            clearTimeout(this.intermittentTimer);
            this.intermittentTimer = null;
        }
        // Leave intermittentIsOn in a defined state (true) so next start begins with "on"
        this.intermittentIsOn = true;
    }

    /**
     * Check if audio is started
     * @returns {boolean} True if audio is started
     */
    getIsStarted() {
        return this.isStarted;
    }

    /**
     * Set sine wave volume in dB
     * @param {number} dbValue - Volume in decibels
     */
    setSineVolume(dbValue: number): void {
        const gain = this.dbToGain(dbValue);
        // Store the requested target gain so intermittent toggling can use it
        this.sineTargetGain = gain;

        if (!this.sineGainNode || !this.audioContext) return;

        // If we're in continuous mode, apply the target immediately.
        if (this.playbackMode === 'continuous') {
            this.sineGainNode.gain.setTargetAtTime(gain, this.audioContext.currentTime, 0.015);
        } else {
            // Intermittent mode: only apply the gain when currently in the "on" phase.
            if (this.intermittentIsOn) {
                this.sineGainNode.gain.setTargetAtTime(gain, this.audioContext.currentTime, 0.015);
            }
            // If currently off, we'll apply the stored gain on the next "on" cycle.
        }
    }

    /**
     * Convert dB to linear gain
     * @param {number} db - Decibel value
     * @returns {number} Linear gain
     */
    dbToGain(db: number): number {
        return mathDbToGain(db);
    }
}

// ES Module Export
export { AudioEngine };
export default AudioEngine;
